import { PHYSICS, WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import { vec2 } from '../math/vec2.ts';
import { collideCircle, type Collider } from './Collision.ts';
import type { MagneticField } from './MagneticField.ts';

/**
 * Ferrofluid, approximated.
 *
 * Not SPH and definitely not Navier-Stokes: particles with three cheap pairwise
 * behaviours — cohesion (pull), separation (push) and viscosity (velocity matching) —
 * integrated semi-implicitly and then relaxed with position-based constraints. That
 * combination is unconditionally stable at our step size and reads as thick oily liquid,
 * which is the only thing the prototype needs it to do.
 *
 * State lives in flat typed arrays; nothing here allocates per frame.
 */

/** How many angular sectors the ball's coverage estimate is bucketed into. */
export const COVERAGE_SECTORS = 12;

/** The ball, as the fluid sees it: an input circle and a set of accumulated reactions. */
export interface FluidBallProbe {
  x: number;
  y: number;
  radius: number;
  /** Net push from penetrating particles (world units, pre-scaling). */
  pushX: number;
  pushY: number;
  /** Mean velocity of the fluid touching the ball. */
  fluidVelX: number;
  fluidVelY: number;
  contacts: number;
  /** Bitmask of occupied sectors, for the coverage metric. */
  sectors: number;
}

export function createBallProbe(): FluidBallProbe {
  return {
    x: 0,
    y: 0,
    radius: 0,
    pushX: 0,
    pushY: 0,
    fluidVelX: 0,
    fluidVelY: 0,
    contacts: 0,
    sectors: 0,
  };
}

/** Margin around the world included in the spatial hash, so escapees still bucket. */
const GRID_MARGIN = 120;

const scratchForce = vec2();

export class Ferrofluid {
  readonly capacity: number;
  count = 0;

  /** Mean particle speed over the last step, in wu/s. Drives the audio and effects. */
  meanSpeed = 0;

  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;

  /** Positions at the start of the current tick, so the renderer can interpolate. */
  readonly tickStartX: Float32Array;
  readonly tickStartY: Float32Array;

  private readonly prevX: Float32Array;
  private readonly prevY: Float32Array;
  private readonly ax: Float32Array;
  private readonly ay: Float32Array;
  private readonly dvx: Float32Array;
  private readonly dvy: Float32Array;
  /** Contact normal recorded during constraint solving, for the friction pass. */
  private readonly contactX: Float32Array;
  private readonly contactY: Float32Array;

  // --- spatial hash ---
  private readonly cellSize: number;
  private readonly gridWidth: number;
  private readonly gridHeight: number;
  private readonly cellOf: Int32Array;
  private readonly cellStart: Int32Array;
  private readonly cellCursor: Int32Array;
  private readonly sorted: Int32Array;

  constructor(capacity: number) {
    this.capacity = capacity;

    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.tickStartX = new Float32Array(capacity);
    this.tickStartY = new Float32Array(capacity);
    this.prevX = new Float32Array(capacity);
    this.prevY = new Float32Array(capacity);
    this.ax = new Float32Array(capacity);
    this.ay = new Float32Array(capacity);
    this.dvx = new Float32Array(capacity);
    this.dvy = new Float32Array(capacity);
    this.contactX = new Float32Array(capacity);
    this.contactY = new Float32Array(capacity);

    this.cellSize = PHYSICS.particleInteractionRadius;
    this.gridWidth = Math.ceil((WORLD_WIDTH + GRID_MARGIN * 2) / this.cellSize);
    this.gridHeight = Math.ceil((WORLD_HEIGHT + GRID_MARGIN * 2) / this.cellSize);

    const cells = this.gridWidth * this.gridHeight;
    this.cellOf = new Int32Array(capacity);
    this.cellStart = new Int32Array(cells + 1);
    this.cellCursor = new Int32Array(cells + 1);
    this.sorted = new Int32Array(capacity);
  }

  reset(): void {
    this.count = 0;
  }

  /** Add one particle. Returns false when the buffer is full. */
  spawn(x: number, y: number, vx = 0, vy = 0): boolean {
    if (this.count >= this.capacity) return false;
    const i = this.count++;
    this.px[i] = x;
    this.py[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.prevX[i] = x;
    this.prevY[i] = y;
    this.tickStartX[i] = x;
    this.tickStartY[i] = y;
    return true;
  }

  /** Advance the fluid by dt, in PHYSICS.fluidSubSteps equal sub-steps. */
  step(
    dt: number,
    field: MagneticField,
    colliders: readonly Collider[],
    ballProbe: FluidBallProbe | null,
  ): void {
    if (this.count === 0) return;

    const count = this.count;
    this.tickStartX.set(this.px.subarray(0, count));
    this.tickStartY.set(this.py.subarray(0, count));

    const subSteps = Math.max(1, PHYSICS.fluidSubSteps | 0);
    const subDt = dt / subSteps;

    if (ballProbe) {
      ballProbe.pushX = 0;
      ballProbe.pushY = 0;
      ballProbe.fluidVelX = 0;
      ballProbe.fluidVelY = 0;
      ballProbe.contacts = 0;
      ballProbe.sectors = 0;
    }

    for (let s = 0; s < subSteps; s++) {
      this.buildGrid();
      this.accumulatePairs(field);
      this.integrate(subDt);
      this.solveConstraints(subDt, colliders, ballProbe);
    }
  }

  // -------------------------------------------------------------------------
  // spatial hash
  // -------------------------------------------------------------------------

  private buildGrid(): void {
    const { cellSize, gridWidth, gridHeight, cellOf, cellStart, cellCursor, sorted } = this;
    const count = this.count;
    const cells = gridWidth * gridHeight;

    cellStart.fill(0, 0, cells + 1);

    for (let i = 0; i < count; i++) {
      let gx = Math.floor((this.px[i] + GRID_MARGIN) / cellSize);
      let gy = Math.floor((this.py[i] + GRID_MARGIN) / cellSize);
      gx = gx < 0 ? 0 : gx >= gridWidth ? gridWidth - 1 : gx;
      gy = gy < 0 ? 0 : gy >= gridHeight ? gridHeight - 1 : gy;
      const cell = gy * gridWidth + gx;
      cellOf[i] = cell;
      cellStart[cell + 1]++;
    }

    for (let c = 1; c <= cells; c++) cellStart[c] += cellStart[c - 1];
    cellCursor.set(cellStart.subarray(0, cells + 1));

    for (let i = 0; i < count; i++) sorted[cellCursor[cellOf[i]]++] = i;
  }

  // -------------------------------------------------------------------------
  // forces
  // -------------------------------------------------------------------------

  /**
   * One neighbour sweep, two results.
   *
   * Cohesion (a force) and viscosity (a velocity nudge) need exactly the same neighbour
   * set, and the sweep is the expensive part — so they share it. Every pair is visited
   * once and gets equal and opposite contributions, so the blob has no net
   * self-propulsion.
   */
  private accumulatePairs(field: MagneticField): void {
    const count = this.count;
    const { ax, ay, dvx, dvy, px, py, vx, vy, cellOf, cellStart, sorted, gridWidth, gridHeight } =
      this;
    const { gravity, cohesion, viscosity, particleInteractionRadius } = PHYSICS;
    const interaction = particleInteractionRadius;
    const interactionSq = interaction * interaction;

    for (let i = 0; i < count; i++) {
      field.sampleForce(px[i], py[i], scratchForce);
      ax[i] = scratchForce.x;
      ay[i] = scratchForce.y + gravity;
    }
    dvx.fill(0, 0, count);
    dvy.fill(0, 0, count);

    for (let i = 0; i < count; i++) {
      const cell = cellOf[i];
      const gx = cell % gridWidth;
      const gy = (cell - gx) / gridWidth;
      const x = px[i];
      const y = py[i];

      const minY = gy > 0 ? gy - 1 : 0;
      const maxY = gy < gridHeight - 1 ? gy + 1 : gridHeight - 1;
      const minX = gx > 0 ? gx - 1 : 0;
      const maxX = gx < gridWidth - 1 ? gx + 1 : gridWidth - 1;

      for (let ny = minY; ny <= maxY; ny++) {
        const rowStart = ny * gridWidth;
        for (let nx = minX; nx <= maxX; nx++) {
          const neighbour = rowStart + nx;
          const end = cellStart[neighbour + 1];
          for (let s = cellStart[neighbour]; s < end; s++) {
            const j = sorted[s];
            if (j <= i) continue;
            const dx = px[j] - x;
            const dy = py[j] - y;
            const distanceSq = dx * dx + dy * dy;
            if (distanceSq >= interactionSq || distanceSq < 1e-8) continue;

            const distance = Math.sqrt(distanceSq);
            const falloff = 1 - distance / interaction;

            // Cohesion: a gentle pull that keeps the blob a blob.
            const magnitude = (cohesion * falloff * falloff) / distance;
            const fx = dx * magnitude;
            const fy = dy * magnitude;
            ax[i] += fx;
            ay[i] += fy;
            ax[j] -= fx;
            ay[j] -= fy;

            // Viscosity: XSPH-style velocity matching — the "thick oil" knob.
            const blend = viscosity * falloff * 0.5;
            const rvx = (vx[j] - vx[i]) * blend;
            const rvy = (vy[j] - vy[i]) * blend;
            dvx[i] += rvx;
            dvy[i] += rvy;
            dvx[j] -= rvx;
            dvy[j] -= rvy;
          }
        }
      }
    }
  }

  private integrate(dt: number): void {
    const count = this.count;
    const { px, py, vx, vy, ax, ay, dvx, dvy, prevX, prevY, contactX, contactY } = this;
    // Damping is configured per second so it does not silently change meaning when the
    // sub-step count is tuned.
    const damping = Math.pow(PHYSICS.fluidDamping, dt);
    const maxSpeed = PHYSICS.fluidMaxSpeed;
    const maxSpeedSq = maxSpeed * maxSpeed;

    for (let i = 0; i < count; i++) {
      let nvx = (vx[i] + ax[i] * dt) * damping + dvx[i];
      let nvy = (vy[i] + ay[i] * dt) * damping + dvy[i];

      const speedSq = nvx * nvx + nvy * nvy;
      if (speedSq > maxSpeedSq) {
        const k = maxSpeed / Math.sqrt(speedSq);
        nvx *= k;
        nvy *= k;
      }

      vx[i] = nvx;
      vy[i] = nvy;
      prevX[i] = px[i];
      prevY[i] = py[i];
      px[i] += nvx * dt;
      py[i] += nvy * dt;
      contactX[i] = 0;
      contactY[i] = 0;
    }
  }

  // -------------------------------------------------------------------------
  // constraints
  // -------------------------------------------------------------------------

  private solveConstraints(
    dt: number,
    colliders: readonly Collider[],
    ballProbe: FluidBallProbe | null,
  ): void {
    const iterations = Math.max(1, PHYSICS.relaxIterations | 0);
    for (let iteration = 0; iteration < iterations; iteration++) {
      const last = iteration === iterations - 1;
      this.solveSeparation();
      this.solveColliders(colliders);
      if (ballProbe) this.solveBall(ballProbe, last);
    }
    this.finaliseVelocities(dt);
  }

  /** Push overlapping neighbours apart. This is what stops the blob collapsing to a dot. */
  private solveSeparation(): void {
    const count = this.count;
    const { px, py, cellOf, cellStart, sorted, gridWidth, gridHeight } = this;
    const rest = PHYSICS.particleRestDistance;
    const restSq = rest * rest;
    const stiffness = PHYSICS.relaxStiffness;

    for (let i = 0; i < count; i++) {
      const cell = cellOf[i];
      const gx = cell % gridWidth;
      const gy = (cell - gx) / gridWidth;

      const minY = gy > 0 ? gy - 1 : 0;
      const maxY = gy < gridHeight - 1 ? gy + 1 : gridHeight - 1;
      const minX = gx > 0 ? gx - 1 : 0;
      const maxX = gx < gridWidth - 1 ? gx + 1 : gridWidth - 1;

      for (let ny = minY; ny <= maxY; ny++) {
        const rowStart = ny * gridWidth;
        for (let nx = minX; nx <= maxX; nx++) {
          const neighbour = rowStart + nx;
          const end = cellStart[neighbour + 1];
          for (let s = cellStart[neighbour]; s < end; s++) {
            const j = sorted[s];
            if (j <= i) continue;

            let dx = px[j] - px[i];
            let dy = py[j] - py[i];
            let distanceSq = dx * dx + dy * dy;
            if (distanceSq >= restSq) continue;

            if (distanceSq < 1e-8) {
              // Perfectly coincident: nudge along a deterministic axis derived from the
              // indices, so the pair separates without needing randomness.
              dx = ((i * 37 + j * 17) % 7) - 3;
              dy = ((i * 11 + j * 29) % 7) - 3;
              distanceSq = dx * dx + dy * dy;
              if (distanceSq < 1e-8) {
                dx = 1;
                dy = 0;
                distanceSq = 1;
              }
            }

            const distance = Math.sqrt(distanceSq);
            const correction = ((rest - distance) / distance) * 0.5 * stiffness;
            const cx = dx * correction;
            const cy = dy * correction;

            px[i] -= cx;
            py[i] -= cy;
            px[j] += cx;
            py[j] += cy;
          }
        }
      }
    }
  }

  private solveColliders(colliders: readonly Collider[]): void {
    const count = this.count;
    const { px, py, contactX, contactY } = this;
    const radius = PHYSICS.particleRadius;
    const colliderCount = colliders.length;

    for (let i = 0; i < count; i++) {
      const x = px[i];
      const y = py[i];
      for (let c = 0; c < colliderCount; c++) {
        const contact = collideCircle(colliders[c], px[i], py[i], radius);
        if (!contact) continue;
        px[i] += contact.nx * contact.depth;
        py[i] += contact.ny * contact.depth;
        contactX[i] = contact.nx;
        contactY[i] = contact.ny;
      }
      // Guard against NaN creeping in from a degenerate collider.
      if (!Number.isFinite(px[i]) || !Number.isFinite(py[i])) {
        px[i] = x;
        py[i] = y;
      }
    }
  }

  /**
   * Push particles out of the ball, and record the reaction so the ball can be shoved,
   * dragged and floated by the fluid.
   */
  private solveBall(probe: FluidBallProbe, record: boolean): void {
    const count = this.count;
    const { px, py, vx, vy } = this;
    const radius = probe.radius + PHYSICS.particleRadius;
    const radiusSq = radius * radius;
    const sectorScale = COVERAGE_SECTORS / (Math.PI * 2);

    for (let i = 0; i < count; i++) {
      const dx = px[i] - probe.x;
      const dy = py[i] - probe.y;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq >= radiusSq) continue;

      const distance = Math.sqrt(distanceSq);
      let nx: number;
      let ny: number;
      if (distance < 1e-6) {
        nx = 0;
        ny = -1;
      } else {
        nx = dx / distance;
        ny = dy / distance;
      }
      const depth = radius - distance;

      px[i] += nx * depth;
      py[i] += ny * depth;

      if (!record) continue;

      // Reaction on the ball points into the ball, i.e. opposite the particle's escape.
      probe.pushX -= nx * depth;
      probe.pushY -= ny * depth;
      probe.fluidVelX += vx[i];
      probe.fluidVelY += vy[i];
      probe.contacts++;

      let sector = Math.floor((Math.atan2(ny, nx) + Math.PI) * sectorScale);
      if (sector < 0) sector = 0;
      if (sector >= COVERAGE_SECTORS) sector = COVERAGE_SECTORS - 1;
      probe.sectors |= 1 << sector;
    }

    if (record && probe.contacts > 0) {
      probe.fluidVelX /= probe.contacts;
      probe.fluidVelY /= probe.contacts;
    }
  }

  /**
   * Position-based velocity update: whatever the constraints did to the positions is
   * exactly what should have happened to the velocities.
   */
  private finaliseVelocities(dt: number): void {
    const count = this.count;
    const { px, py, vx, vy, prevX, prevY, contactX, contactY } = this;
    const inverseDt = 1 / dt;
    // Configured per second so the sub-step count cannot silently change how sticky
    // walls are.
    const friction = Math.pow(PHYSICS.fluidWallFriction, dt);
    const restitution = PHYSICS.fluidWallRestitution;
    let speedSum = 0;

    for (let i = 0; i < count; i++) {
      let nvx = (px[i] - prevX[i]) * inverseDt;
      let nvy = (py[i] - prevY[i]) * inverseDt;

      const nx = contactX[i];
      const ny = contactY[i];
      if (nx !== 0 || ny !== 0) {
        // Split into normal and tangential parts so walls can be sticky without being
        // bouncy — ferrofluid should cling, not ping.
        const normalSpeed = nvx * nx + nvy * ny;
        const tangentX = nvx - nx * normalSpeed;
        const tangentY = nvy - ny * normalSpeed;
        const bounce = normalSpeed < 0 ? -normalSpeed * restitution : normalSpeed;
        nvx = tangentX * friction + nx * bounce;
        nvy = tangentY * friction + ny * bounce;
      }

      vx[i] = nvx;
      vy[i] = nvy;
      speedSum += Math.abs(nvx) + Math.abs(nvy);
    }

    // Manhattan length is close enough for an audio envelope and avoids n square roots.
    this.meanSpeed = count > 0 ? (speedSum / count) * 0.79 : 0;
  }
}
